import type { MaybeRefOrGetter } from '@vueuse/core';
import type { TitleDetail } from '@/interfaces/title-details-graphql';
import type {
	GetOneSimilarTitleQuery,
	GetOneSimilarTitleQueryVariables,
} from '@/components/graphql/queries/GetOneSimilarTitle.query';
import type { ValidExperimentName } from '@/libraries/experiment';

import { GetOneSimilarTitleDocument } from '@/components/graphql/queries/GetOneSimilarTitle.query';
import { genresExclusion, audienceTags } from './config';

import { useExperiment } from '@/libraries/experiment';
import { useI18n } from '@/helpers/composables/useI18n';
import { useQuery } from '@/helpers/composables/useApollo';
import { toCatalogueItem } from '@/helpers/promotion-helper';
import { toValue } from '@vueuse/core';

import { computed } from 'vue';
import { useLanguageStore } from '@/stores/pinia/useLanguageStore';
import { storeToRefs } from 'pinia';
import { PPSwiperRule } from '@/components/experiments/LowPerfExp';

/*
 * SQTV SR Experiment Configuration
 *
 * Change the values of the constants here for your SQTV SR.
 * Update the export name aliases to avoid conflicts.
 */

const EXPERIMENT_NAME: ValidExperimentName = 'HACK_SQTV_SR_BR_2';
const TARGET_TITLE_ID = 'ts328603'; /* Alien: Earth */
const TARGET_COUNTRY = 'BR';
const TARGET_PROVIDER = 'dnp'; /* Disney+ */
const AFFILIATE_URL = 'https://disneyplus.bn5x.net/c/1206980/564546/9358?sharedid=Huluween';
const CATALOGUE_ITEM = {
	packageId: 337,
	clearName: 'Disney+',
	technicalName: 'disneyplus',
	shortName: 'dnp',
	icon: '/icon/313118777/s100/disneyplus.{format}',
	iconWide: '/icon_wide/322151142/s160/disneyplus.{format}',
} as const;

export { useSqtvSr as useBrSqtvSr, isSqtvSrActive as isBrSqtvActive };

function useSqtvSr({ title }: { title: MaybeRefOrGetter<TitleDetail> }) {
	const { activeVariantWithControlGroup: activeVariant } = useExperiment(EXPERIMENT_NAME);

	const { language, country } = storeToRefs(useLanguageStore());

	const showSQTV = computed(() => activeVariant.value === 'control');

	const { result } = useQuery<GetOneSimilarTitleQuery, GetOneSimilarTitleQueryVariables>(
		GetOneSimilarTitleDocument,
		() => ({
			titleId: TARGET_TITLE_ID,
			country: country.value,
			language: language.value,
			offerFilters: {},
		}),
		() => ({ enabled: showSQTV.value })
	);

	const { t } = useI18n();
	const overrides = {
		headline: t('WEBAPP_WATCH_SIMILAR_TITLES_ON_PROVIDER', { provider: 'Disney+' }),
	};

	const targetTitle = computed(() => {
		if (!showSQTV.value) return null;

		return result.value ?? null;
	});

	const catalogueItem = computed(() => {
		if (!showSQTV.value) return null;

		return makeCatalogueItem(toValue(title), country.value);
	});

	const swiperConfig = computed<PPSwiperRule | null>(() => {
		if (!showSQTV.value) return null;

		return {
			rules: [],
			slideDelay: 1500,
			default: { id: 'sqtv_sr' },
			packages: [
				{ id: 'default', placement: 'ft' },
				{ id: 'sqtv_sr', placement: 'sqtv', shortName: TARGET_PROVIDER },
			],
		};
	});

	return {
		AFFILIATE_URL,
		showSQTV,
		targetTitle,
		catalogueItem,
		swiperConfig,
		overrides,
	};
}

export function isSqtvSrActive(title: TitleDetail, country: string) {
	// Disney people already know about Only Murders in the Building
	const titleProviders = new Set(title.offers.map(offer => offer.package.shortName));
	const isNotProvider = !titleProviders.has(TARGET_PROVIDER);

	const titleTags = title.content.tags ?? [];
	const titleGenres = title.content.genres ?? [];

	return (
		country === TARGET_COUNTRY &&
		isNotProvider &&
		(titleTags.length === 0 || titleTags.some(tag => audienceTags.has(tag.technicalName))) &&
		titleGenres.every(genre => !genresExclusion.has(genre.shortName))
	);
}

function makeCatalogueItem(title: TitleDetail, country: string) {
	if (!isSqtvSrActive(title, country)) return null;

	const item = toCatalogueItem(CATALOGUE_ITEM, { country, title });
	item.placement = 'sqtv';

	return item;
}
